import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import { joinDuel, leaveDuel } from "../lib/api";
import { enablePush, pushPermission } from "../lib/push";
import { friendly } from "../lib/errors";

/** 1v1 Relay queue: pair with one compatible person and play head-to-head. */
export default function DuelQueue() {
  const { uid } = useAuth();
  const navigate = useNavigate();
  const [queued, setQueued] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) return;
    return onSnapshot(doc(db, "users", uid), async (snap) => {
      const d = snap.data();
      if (!d?.activeDuelId) return;
      // Only enter a live duel I haven't already decided on (see Relay.tsx).
      const duel = (await getDoc(doc(db, "duels", d.activeDuelId as string))).data();
      if (duel && duel.state === "live" && duel.choices?.[uid] === undefined) {
        navigate(`/relay/duel/${d.activeDuelId}`, { replace: true });
      }
    });
  }, [uid, navigate]);

  useEffect(() => {
    if (!uid) return;
    return onSnapshot(
      doc(db, "duelQueue", uid),
      (snap) => setQueued(snap.exists()),
      () => setQueued(false),
    );
  }, [uid]);

  async function join() {
    // Ask for notifications right here (a real user gesture, which iOS requires)
    // so the "your 1v1 is on!" push can actually reach you while you wait. Fire
    // and forget — never block joining on it, and don't re-prompt once decided.
    if (uid && pushPermission() === "default") void enablePush(uid).catch(() => {});
    setBusy(true);
    setError(null);
    try {
      try {
        await joinDuel();
      } catch (e) {
        // Right after match/pass, the decision transaction (which clears the
        // relay flag server-side) can still be committing when this call lands.
        // One short retry absorbs that race instead of surfacing a phantom
        // "already in a Relay" — anything else rethrows immediately.
        if (!String((e as Error)?.message ?? e).includes("already in a Relay")) throw e;
        await new Promise((r) => setTimeout(r, 1500));
        await joinDuel();
      }
    } catch (e) {
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  async function leave() {
    setBusy(true);
    setError(null);
    try {
      await leaveDuel();
    } catch (e) {
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen">
      <Link to="/relay" className="chat-back" style={{ textDecoration: "none" }}>‹ Relay</Link>
      <h1>1v1</h1>
      <p className="muted">
        Head-to-head with one compatible person: a 60-second lobby, a quick game, then you
        each privately choose — match or pass. If you both say yes, it's a match… and the
        loser sends the first message.
      </p>

      {queued === null ? (
        <div className="splash" style={{ height: 160 }}><div className="spinner" /></div>
      ) : queued ? (
        <div className="relay-queue-box">
          <div className="pulse-ring" />
          <h2 style={{ marginTop: 18 }}>Finding your opponent…</h2>
          <p className="muted">You'll drop in automatically the moment someone compatible joins.</p>
          <button className="btn btn-secondary" disabled={busy} onClick={() => void leave()}>
            Leave the queue
          </button>
        </div>
      ) : (
        <>
          <div className="relay-how">
            <div className="how-step"><span className="how-num">1</span> Get paired with one person</div>
            <div className="how-step"><span className="how-num">2</span> Play a quick head-to-head game</div>
            <div className="how-step"><span className="how-num">3</span> Both say yes? It's a match — loser texts first</div>
          </div>
          <button className="btn" disabled={busy} onClick={() => void join()}>
            {busy ? "Joining…" : "Find an opponent"}
          </button>
        </>
      )}
      {error && <div className="error">{error}</div>}
    </div>
  );
}
